// Dépenses — suivi et approbation des dépenses
import { useState, useCallback, useMemo } from "react";
import {
  ArrowDownCircle,
  AlertCircle,
  CheckCircle,
  XCircle,
  Plus,
  ExternalLink,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  useExpenses,
  useCreateExpense,
  useApproveExpense,
  useRejectExpense,
} from "@/hooks/admin/useFinance";
import type {
  Expense,
  ExpenseCategory,
  ExpenseStatus,
  CreateExpenseInput,
} from "@/lib/admin-api/finance";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<ExpenseStatus, string> = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

const STATUS_LABELS: Record<ExpenseStatus, string> = {
  pending: "En attente",
  approved: "Approuvée",
  rejected: "Rejetée",
};

const CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  transport: "Transport",
  salaries: "Salaires",
  marketing: "Marketing",
  operations: "Opérations",
  other: "Autre",
};

const CATEGORY_OPTIONS: readonly { value: ExpenseCategory; label: string }[] = [
  { value: "transport", label: "Transport" },
  { value: "salaries", label: "Salaires" },
  { value: "marketing", label: "Marketing" },
  { value: "operations", label: "Opérations" },
  { value: "other", label: "Autre" },
];

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(amount: number): string {
  return `${amount.toLocaleString("fr-SN")} FCFA`;
}

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Create expense modal
// ---------------------------------------------------------------------------

interface CreateModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (input: CreateExpenseInput) => Promise<void>;
  readonly isSubmitting: boolean;
}

function CreateExpenseModal({
  open,
  onClose,
  onSubmit,
  isSubmitting,
}: CreateModalProps) {
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<ExpenseCategory>("operations");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [receiptUrl, setReceiptUrl] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setDescription("");
    setCategory("operations");
    setAmount("");
    setDate(new Date().toISOString().split("T")[0]);
    setReceiptUrl("");
    setError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!description.trim()) {
      setError("La description est obligatoire.");
      return;
    }
    const amountNum = Number(amount);
    if (!amount || isNaN(amountNum) || amountNum <= 0) {
      setError("Montant invalide.");
      return;
    }
    if (!date) {
      setError("La date est obligatoire.");
      return;
    }
    setError(null);
    try {
      const input: CreateExpenseInput = {
        description: description.trim(),
        category,
        amount_fcfa: amountNum,
        date,
        ...(receiptUrl.trim() ? { receipt_url: receiptUrl.trim() } : {}),
      };
      await onSubmit(input);
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Une erreur est survenue.",
      );
    }
  }, [description, category, amount, date, receiptUrl, onSubmit, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Nouvelle dépense"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} className="gap-1.5">
            {isSubmitting && (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Description <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description de la dépense…"
            className="h-9 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Catégorie <span className="text-red-500">*</span>
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
            className="h-9 w-full rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Montant (FCFA) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min={0}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
            className="h-9 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-9 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            URL du reçu (facultatif)
          </label>
          <input
            type="url"
            value={receiptUrl}
            onChange={(e) => setReceiptUrl(e.target.value)}
            placeholder="https://…"
            className="h-9 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Expenses() {
  const [categoryFilter, setCategoryFilter] = useState<ExpenseCategory | "">("");
  const [statusFilter, setStatusFilter] = useState<ExpenseStatus | "">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [page, setPage] = useState(1);

  const params = useMemo(
    () => ({
      category: categoryFilter || null,
      status: statusFilter || null,
      date_from: dateFrom || null,
      date_to: dateTo || null,
      page,
      limit: 50,
    }),
    [categoryFilter, statusFilter, dateFrom, dateTo, page],
  );

  const { data, isLoading, isError, refetch } = useExpenses(params);
  const createExpense = useCreateExpense();
  const approveExpense = useApproveExpense();
  const rejectExpense = useRejectExpense();

  const expenses = data?.expenses ?? [];

  const pending = useMemo(
    () => expenses.filter((e) => e.status === "pending").length,
    [expenses],
  );
  const approved = useMemo(
    () => expenses.filter((e) => e.status === "approved").length,
    [expenses],
  );
  const rejected = useMemo(
    () => expenses.filter((e) => e.status === "rejected").length,
    [expenses],
  );

  const handleCreate = useCallback(
    async (input: CreateExpenseInput) => {
      await createExpense.mutateAsync(input);
    },
    [createExpense],
  );

  const columns: readonly ColumnDef<Expense>[] = useMemo(
    () => [
      {
        id: "description",
        header: "Description",
        cell: (e) => (
          <span className="text-sm font-medium text-stone-900">
            {e.description}
          </span>
        ),
      },
      {
        id: "category",
        header: "Catégorie",
        cell: (e) => (
          <span className="rounded-full bg-stone-100 px-2.5 py-0.5 text-xs font-medium text-stone-700">
            {CATEGORY_LABELS[e.category]}
          </span>
        ),
      },
      {
        id: "amount_fcfa",
        header: "Montant",
        cell: (e) => (
          <span className="text-sm font-semibold text-stone-900">
            {formatFcfa(e.amount_fcfa)}
          </span>
        ),
      },
      {
        id: "date",
        header: "Date",
        cell: (e) => (
          <span className="text-sm text-stone-500">{formatDate(e.date)}</span>
        ),
      },
      {
        id: "approved_by",
        header: "Soumis / approuvé par",
        cell: (e) => (
          <span className="text-sm text-stone-600">
            {e.approved_by ?? "—"}
          </span>
        ),
      },
      {
        id: "status",
        header: "Statut",
        cell: (e) => (
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[e.status]}`}
          >
            {STATUS_LABELS[e.status]}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (e) => (
          <div className="flex items-center gap-1">
            {e.status === "pending" && (
              <>
                <button
                  type="button"
                  onClick={() => approveExpense.mutate(e.id)}
                  disabled={approveExpense.isPending}
                  className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                >
                  <CheckCircle className="h-3.5 w-3.5" />
                  Approuver
                </button>
                <button
                  type="button"
                  onClick={() => rejectExpense.mutate(e.id)}
                  disabled={rejectExpense.isPending}
                  className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <XCircle className="h-3.5 w-3.5" />
                  Rejeter
                </button>
              </>
            )}
            {e.receipt_url && (
              <a
                href={e.receipt_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-stone-600 hover:bg-stone-50"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Reçu
              </a>
            )}
          </div>
        ),
      },
    ],
    [approveExpense, rejectExpense],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <ArrowDownCircle className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Dépenses
              </h1>
              <p className="text-sm text-stone-500">
                {isLoading
                  ? "Chargement…"
                  : `${data?.pagination.total ?? 0} dépense(s)`}
              </p>
            </div>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" />
            Nouvelle dépense
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KPICard
            label="Total ce mois"
            value={data?.total_fcfa ?? 0}
            format="currency"
            icon={<ArrowDownCircle className="h-4 w-4" />}
          />
          <KPICard
            label="En attente d'approbation"
            value={pending}
            format="number"
            icon={<AlertCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Approuvées"
            value={approved}
            format="number"
            icon={<CheckCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Rejetées"
            value={rejected}
            format="number"
            icon={<XCircle className="h-4 w-4" />}
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value as ExpenseCategory | "");
              setPage(1);
            }}
            className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">Toutes catégories</option>
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as ExpenseStatus | "");
              setPage(1);
            }}
            className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="approved">Approuvée</option>
            <option value="rejected">Rejetée</option>
          </select>
          <div className="flex items-center gap-1.5">
            <label className="text-sm font-medium text-stone-600">Du :</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-sm font-medium text-stone-600">Au :</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
          {(categoryFilter || statusFilter || dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => {
                setCategoryFilter("");
                setStatusFilter("");
                setDateFrom("");
                setDateTo("");
                setPage(1);
              }}
              className="text-sm text-stone-500 hover:text-stone-700"
            >
              Réinitialiser
            </button>
          )}
        </div>

        {/* Mutation feedback */}
        {(approveExpense.isSuccess || rejectExpense.isSuccess || createExpense.isSuccess) && (
          <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            <CheckCircle className="h-4 w-4 shrink-0" />
            <span>Opération effectuée avec succès.</span>
          </div>
        )}

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les dépenses.</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="ml-auto rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {!isLoading && expenses.length === 0 ? (
              <EmptyState
                icon={<ArrowDownCircle className="h-6 w-6" />}
                title="Aucune dépense"
                description="Les dépenses enregistrées apparaîtront ici."
              />
            ) : (
              <AdminDataTable
                data={expenses}
                columns={columns}
                isLoading={isLoading}
                pagination={
                  data?.pagination
                    ? {
                        page: data.pagination.page,
                        limit: data.pagination.limit,
                        total: data.pagination.total,
                      }
                    : undefined
                }
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>

      {/* Create modal */}
      <CreateExpenseModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={handleCreate}
        isSubmitting={createExpense.isPending}
      />
    </AdminLayout>
  );
}
